import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { shareWhatsApp } from "@/lib/whatsapp";
import { CheckCircle2, Printer, MessageCircle, Plane, Loader2, Mail } from "lucide-react";
import { motion } from "framer-motion";

export default function Voucher() {
  const { ref } = useParams();
  const { t, lang, money } = useApp();
  const [b, setB] = useState(null);

  useEffect(() => { api.get(`/bookings/${ref}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [ref]);

  if (b === null) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-tigris" size={40} /></div>;
  if (b === false) return <div className="text-center py-32 text-slate-400">404</div>;

  const share = () => {
    const msg = lang === "ar"
      ? `✅ تم تأكيد الحجز!\n📋 ${b.title}\n🔖 رمز الحجز: ${b.ref}\n💰 ${money(b.amount)}\nسفير بغداد`
      : `✅ Booking confirmed!\n📋 ${b.title}\n🔖 Ref: ${b.ref}\n💰 ${money(b.amount)}\nSafeer Baghdad`;
    shareWhatsApp(msg);
  };

  return (
    <div className="bg-sand min-h-screen py-12 px-4">
      <div className="max-w-xl mx-auto">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-tigris text-white p-8 text-center">
            <CheckCircle2 size={56} className="mx-auto mb-3 text-gold" />
            <h1 className="text-2xl font-bold font-display">{t.voucher.congrats}</h1>
            <p className="text-white/80 text-sm mt-2 flex items-center justify-center gap-1.5"><Mail size={14} /> {t.voucher.emailSent}</p>
          </div>
          <div className="p-8">
            <div className="flex items-center justify-between border-b border-dashed border-slate-200 pb-5 mb-5">
              <div>
                <div className="text-xs text-slate-400">{t.voucher.ref}</div>
                <div className="text-2xl font-extrabold text-tigris font-mono">{b.ref}</div>
              </div>
              <span className="w-14 h-14 rounded-2xl bg-tigris/10 text-tigris flex items-center justify-center"><Plane size={26} className="rotate-45" /></span>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-slate-400">{t.voucher.title}</dt><dd className="font-semibold text-slate-800 text-end max-w-[60%]">{b.title}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">{t.voucher.passenger}</dt><dd className="font-semibold text-slate-800">{b.contact_name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">{t.voucher.contact}</dt><dd className="font-semibold text-slate-800" dir="ltr">{b.contact_phone}</dd></div>
              {b.start_date && <div className="flex justify-between"><dt className="text-slate-400">{t.booking.startDate}</dt><dd className="font-semibold text-slate-800">{b.start_date}</dd></div>}
              {b.details?.segments?.length > 0 && (
                <div data-testid="voucher-segments" className="pt-2 border-t border-dashed border-slate-200 space-y-1.5">
                  {b.details.segments.map((s, i) => (
                    <div key={i} className="flex justify-between text-xs" dir="ltr">
                      <span className="font-mono text-slate-500">{i + 1}. {s.origin} → {s.destination}</span>
                      <span className="text-slate-700 font-semibold">{s.date} · {s.depart}–{s.arrive}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between"><dt className="text-slate-400">{t.dashboard.status}</dt>
                <dd><span className={`text-xs font-bold px-3 py-1 rounded-full ${b.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.status === "confirmed" ? t.dashboard.confirmed : t.dashboard.pending}</span></dd>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-3 mt-3"><dt className="text-slate-500 font-bold">{t.booking.total}</dt><dd className="text-xl font-extrabold text-tigris">{money(b.amount)}</dd></div>
            </dl>
            <div className="flex gap-3 mt-7">
              <button onClick={() => window.print()} data-testid="print-voucher" className="btn-primary flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2"><Printer size={17} /> {t.voucher.print}</button>
              <button onClick={share} data-testid="share-voucher" className="btn-wa flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2"><MessageCircle size={17} /> {t.common.shareWhatsapp}</button>
            </div>
            <Link to="/dashboard" className="block text-center text-sm text-tigris font-semibold mt-5 hover:underline">{t.dashboard.title}</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
